---
title: "Risk/Reward Ratio and Win Rate: The Math of Profitability"
slug: risk-reward-and-win-rate
shortTitle: "Risk/Reward & Win Rate"
description: "How the risk/reward ratio and win rate work together, the break-even win rate for any ratio, expectancy, and how trading costs shift the numbers."
level: intermediate
category: trading
course: intermediate
order: 10
topics: [risk-management, trading-strategies]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/risk-reward-and-win-rate.svg
faqs:
  - q: "What is a good risk/reward ratio?"
    a: "There is no single good ratio, because it only means something alongside your win rate. A 1 to 2 ratio needs to win just over a third of the time to break even before costs, while a 1 to 1 ratio needs to win half the time."
  - q: "Is a high win rate a sign of a good strategy?"
    a: "Not on its own. A strategy that wins 70% of the time can still lose money if its average loss is much bigger than its average win. Expectancy, which combines both numbers, is the better measure."
  - q: "Why not just use very distant take-profit levels?"
    a: "Because a distant target is reached less often. Raising the reward per trade usually lowers the win rate, so the ratio and the win rate have to be judged together, with targets placed at levels the market might realistically reach."
  - q: "How many trades do I need before my win rate means anything?"
    a: "More than most people expect. A handful of trades is mostly luck. Many traders look for at least 50 to 100 trades taken under the same rules before drawing conclusions, and treat even that as a rough estimate."
  - q: "Do fees and spreads really make a difference?"
    a: "Yes, especially for short-term trades with small targets. Costs reduce every win and enlarge every loss, which raises the win rate you need to break even."
---

Two traders can both say "I win 60% of my trades" and end the year in completely different places. One lets winners run and cuts losers short; the other banks small gains and holds losers far too long. Win rate alone tells you very little. What matters is how often you win **and** how much you win compared with how much you lose.

This lesson explains the **risk/reward ratio**, the **win rate**, how they combine into **expectancy**, and how trading costs and behavior shift the numbers. It is the arithmetic behind every trading plan, and you can measure your own version from the Closed Positions tab on {{BRAND}}.

<CTA />

<KeyTakeaways>

- **Risk/reward compares distances.** It is the distance to your take profit divided by the distance to your stop loss.
- **Win rate needs context.** A low win rate can work with large winners; a high one can fail with large losers.
- **Break-even has a formula.** The win rate you need to break even is 1 ÷ (1 + reward-to-risk), before costs.
- **Costs and discipline move the math.** Spreads, fees and moved stops quietly change the numbers your plan depends on.

</KeyTakeaways>

## The risk/reward ratio

The **risk/reward ratio** compares what you stand to lose with what you aim to gain on a trade. Risk is the distance from entry to stop loss; reward is the distance from entry to take profit. Traders often call one unit of planned risk **1R**, so a trade aiming for twice its risk is a **2R** target.

<Example title="Risk/reward on ETH/USDT (illustrative prices)">

You buy at 3,000 with a stop loss at 2,940 and a take profit at 3,120.

- Risk: 3,000 − 2,940 = 60 per ETH
- Reward: 3,120 − 3,000 = 120 per ETH
- Ratio: 60 to 120, which is **1 to 2**, or a 2R target

With 0.5 ETH, you would lose about 30 USDT at the stop or gain about 60 USDT at the target, before costs.

</Example>

The ratio is the same whatever your position size. Size decides how many USDT one R is worth; [Position Sizing](/academy/article/position-sizing) shows how to set it.

## Win rate

Your **win rate** is the share of trades that close in profit: wins ÷ total trades. Win 35 out of 100 and your win rate is 35%. On its own, that number cannot tell you whether a strategy makes or loses money.

## How the two fit together

For any reward-to-risk ratio, there is a win rate at which you break even before costs:

**Break-even win rate = 1 ÷ (1 + R)**, where R is the reward divided by the risk.

<CompareTable caption="Break-even win rates before costs">

| Risk to reward | R | Break-even win rate |
| --- | --- | --- |
| 1 to 0.5 | 0.5 | 1 ÷ 1.5 = 66.7% |
| 1 to 1 | 1 | 1 ÷ 2 = 50% |
| 1 to 1.5 | 1.5 | 1 ÷ 2.5 = 40% |
| 1 to 2 | 2 | 1 ÷ 3 = 33.3% |
| 1 to 3 | 3 | 1 ÷ 4 = 25% |

</CompareTable>

A trader aiming for 1 to 3 can be wrong three times out of four and still roughly break even. A trader taking small 1 to 0.5 profits needs to be right two times out of three just to stand still.

## Expectancy: the number that combines them

**Expectancy** is the average result per trade over many trades:

**Expectancy = (win rate × average win) − (loss rate × average loss)**

<Example title="Two very different traders (illustrative numbers)">

**Trader A** wins 40% of trades, with an average win of 100 USDT and an average loss of 50 USDT.

- Expectancy: 0.40 × 100 − 0.60 × 50 = 40 − 30 = **+10 USDT per trade**

**Trader B** wins 70% of trades, with an average win of 30 USDT and an average loss of 80 USDT.

- Expectancy: 0.70 × 30 − 0.30 × 80 = 21 − 24 = **−3 USDT per trade**

The trader who loses more often has the positive number; the one who wins most of the time is slowly losing money.

</Example>

These figures describe past results and averages. A positive expectancy over past trades does not mean future trades will be profitable; markets change, and a small sample can flatter any strategy. [Advanced Risk: Drawdown, Expectancy, Kelly](/academy/article/drawdown-expectancy-kelly) goes further into these limits.

## Costs change the math

Every trade pays the spread and the trade fee ({{TRADE_FEE}}), and positions held overnight may pay a swap. Costs make every win smaller and every loss bigger.

<Example title="Trader A after costs (illustrative numbers)">

Say costs average 5 USDT per trade. Wins become 100 − 5 = 95 USDT and losses become 50 + 5 = 55 USDT.

- Expectancy: 0.40 × 95 − 0.60 × 55 = 38 − 33 = **+5 USDT per trade**

Costs of 5 USDT halved the expectancy. With smaller targets, the same costs would take a bigger share.

</Example>

## The trade-off you cannot escape

It is tempting to set every take profit at 5R. The problem is that a distant target is reached less often, so the win rate falls. Reward-to-risk and win rate are linked; improving one usually costs some of the other. The useful question is not "how high can I set my target?" but "where is a realistic target, such as the next major level, and is the ratio still worth it?"

Low win rates also mean longer losing streaks. With a 40% win rate, the chance that any particular five trades all lose is 0.6 × 0.6 × 0.6 × 0.6 × 0.6, about 7.8%. Over a few hundred trades, a run of seven or eight losses would not be unusual. Your position size has to survive that.

<Warning>

Your ratio only holds if you respect it. Moving a stop loss further away to "give the trade room", removing a take profit or closing winners early all change your real numbers. A stop loss can also fill beyond its price in a fast market or a gap, and leverage makes those slips larger in USDT. Measure what you actually did, not what you planned.

</Warning>

## Measuring your own numbers on {{BRAND}}

Before a trade, the order panel shows the ratio for you. With the **TP / SL** switch on, the **Profit (USDT)** and **Loss (USDT)** fields show what you would make at the take profit and lose at the stop. A profit of 60 against a loss of 30 is a 1 to 2 trade. See [Stop Loss and Take Profit](/academy/article/stop-loss-take-profit) for how those fields work.

After trades close, the **Closed Positions** tab gives you the raw data.

<Screenshot name="closed-positions-tab" alt="The Closed Positions tab with exit price, reason, fees, swap and net PnL" caption="Closed Positions shows each trade's reason, fees, swap and Net PnL." />

<Steps>
<Step title="Collect a sample">

Take at least 30 to 50 trades under the same rules, so the numbers describe your approach rather than luck.

</Step>
<Step title="Count wins and losses">

Use the **Net PnL** column, which already includes **Fees** and **Swap**, to count winning and losing trades. The **Reason** column shows which closed at **Take profit**, at **Stop loss**, or by hand (**Closed**).

</Step>
<Step title="Work out the averages">

Add up the winning Net PnL and divide by the number of wins; do the same for losses.

</Step>
<Step title="Calculate expectancy">

Plug your win rate and averages into the expectancy formula. Record the results in your journal, as described in [Building a Trading Plan and Journal](/academy/article/trading-plan-and-journal).

</Step>
</Steps>

<ProTip>

Record each result in R as well as in USDT: Net PnL divided by the amount you planned to risk. A trade that lost 1.4R tells you a stop slipped or was moved. A habit of winners closing at 0.6R when you planned 2R tells you something about your exits.

</ProTip>

## Conclusion

The risk/reward ratio tells you how much you aim to make for each unit you risk; the win rate tells you how often you get there. Neither means much alone. Together they give your break-even win rate and your expectancy, the average result per trade. Costs push that number down, and moved stops or early exits change it without you noticing. Plan realistic targets, keep your losses at 1R, size so a losing streak is survivable, and measure your real numbers from Closed Positions.
